import React from 'react';
import {
  DndContext,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import {
  AppBadge,
  AppButton,
  AppEmptyState,
  AppInput,
  AppPillSwitch,
  AppSelect,
} from '../../ui';
import MagazineInlineText from './MagazineInlineText';
import MagazineMedia from './MagazineMedia';
import KoikispassDateLink from './KoikispassDateLink';
import SortableMagazineCard from './SortableMagazineCard';
import { resolveAgendaMediaUrl } from '../../../config/agendaApiConfig';
import {
  BUCKET_LABELS,
  bucketVariant,
  canonicalMonth,
  formatMonthLabel,
  formatPrix,
  formatRemise,
} from './magazineHelpers';

function NouveauToggle({ active, editable, disabled, onClick }) {
  if (!editable && !active) return null;

  return (
    <div className="klub-magazine-card__badge">
      <AppBadge
        as={editable ? 'button' : 'span'}
        type={editable ? 'button' : undefined}
        variant="none"
        pill
        className={`klub-magazine-nouveau-tag${active ? ' is-active' : ''}`}
        aria-pressed={editable ? Boolean(active) : undefined}
        disabled={editable ? disabled : undefined}
        title={editable ? (active ? 'Marquer comme déjà client' : 'Marquer comme nouveauté') : undefined}
        onClick={editable ? onClick : undefined}
      >
        {active ? 'Nouveauté' : 'Déjà client'}
      </AppBadge>
    </div>
  );
}

function hasAmount(value) {
  return value != null && String(value).trim() !== '';
}

function OfferPriceTag({ row, editable, busy, onCommit }) {
  const prixEmpty = !hasAmount(row.prix);
  const barreEmpty = !hasAmount(row.prix_barre) || Number(row.prix_barre) <= 0;
  if (prixEmpty && barreEmpty && (!editable || busy)) return null;

  return (
    <div className={`klub-magazine-card__price-tag${prixEmpty && barreEmpty ? ' is-empty' : ''}`}>
      <MagazineInlineText
        value={row.prix ?? ''}
        displayValue={prixEmpty ? '' : formatPrix(row.prix)}
        placeholder="Prix"
        className="klub-magazine-card__price-value"
        type="number"
        disabled={!editable || busy}
        onCommit={(value) => onCommit('prix', value)}
      />
      {!barreEmpty || (editable && !busy) ? (
        <MagazineInlineText
          value={barreEmpty ? '' : row.prix_barre}
          displayValue={barreEmpty ? '' : formatPrix(row.prix_barre)}
          placeholder="Ancien"
          className="klub-magazine-card__price-old"
          emptyClassName="is-placeholder"
          type="number"
          disabled={!editable || busy}
          onCommit={(value) => onCommit('prix_barre', value)}
        />
      ) : null}
    </div>
  );
}

function KlubMagazinePanel({
  tab,
  setTab,
  month,
  setMonth,
  monthOptions,
  busy,
  loading,
  editable,
  sensors,
  commercants,
  offres,
  dates = [],
  setDates = [],
  agendaDates = [],
  isDatesTab = false,
  targetCount,
  setTargetCount,
  targetLabel,
  targetInputId,
  targetMax = 100,
  exportDisabled,
  resolveCommercantStatut,
  resolveOffreStatut,
  onPropose,
  onOpenPicker,
  onExport,
  onRewrite,
  onClearSelection,
  onDragEndCommercants,
  onDragEndOffres,
  onDragEndDates,
  onEditCommercant,
  onEditOffre,
  onEditDate,
  onRewriteDate,
  onRemoveCommercant,
  onRemoveOffre,
  onRemoveDate,
  onInlineSave,
  onToggleNouveau,
}) {
  const xmlExport = isDatesTab;
  return (
    <div className="card kkp-planning-results-card klub-magazine-page mb-24">
      <div className="kkp-planning-view-toolbar">
        <AppPillSwitch
          ariaLabel="Sections magazine"
          value={tab}
          onChange={setTab}
          options={[
            {
              value: 'commercants',
              label: 'Commerçants',
              icon: 'mdi:account-tie',
              count: commercants.length,
            },
            {
              value: 'offres',
              label: 'Offres',
              icon: 'mdi:tag-outline',
              count: offres.length,
            },
            {
              value: 'set',
              label: 'Set de table',
              icon: 'mdi:calendar-month',
              count: setDates.length,
            },
            {
              value: 'agenda',
              label: 'Agenda',
              icon: 'mdi:calendar-export',
              count: agendaDates.length,
            },
          ]}
        />
        <div className="d-flex flex-wrap align-items-center gap-2">
          <AppSelect
            id="magazine-month"
            size="sm"
            value={month}
            onChange={(e) => setMonth(canonicalMonth(e.target.value))}
            aria-label="Parution"
            className="klub-magazine-month-select"
          >
            {monthOptions.map((ym) => (
              <option key={ym} value={ym}>{formatMonthLabel(ym)}</option>
            ))}
          </AppSelect>
          <label className="d-inline-flex align-items-center gap-1 mb-0 small text-secondary-light" htmlFor={targetInputId}>
            {targetLabel}
            <AppInput
              id={targetInputId}
              type="number"
              min={1}
              max={targetMax}
              size="sm"
              className="klub-magazine-count-input"
              value={targetCount}
              onChange={(e) => setTargetCount(e.target.value)}
              title="Nombre pour la proposition auto"
            />
          </label>
          <AppButton
            size="sm"
            variant="outline-primary"
            icon="mdi:auto-fix"
            aria-label="Proposer automatiquement"
            title="Proposer automatiquement"
            disabled={busy}
            onClick={onPropose}
          />
          <AppButton
            size="sm"
            variant="outline-secondary"
            icon={tab === 'commercants' ? 'mdi:account-plus' : (tab === 'offres' ? 'mdi:tag-plus' : 'mdi:calendar-plus')}
            aria-label="Ajouter"
            title="Ajouter"
            disabled={busy}
            onClick={onOpenPicker}
          />
          {isDatesTab ? (
            <AppButton
              size="sm"
              variant="outline-secondary"
              icon="mdi:robot-outline"
              aria-label="Réécrire avec l'IA"
              title="Réécrire toutes les dates avec l'IA"
              disabled={busy || !dates.length}
              onClick={onRewrite}
            />
          ) : null}
          <AppButton
            size="sm"
            variant="outline-secondary"
            icon="mdi:download"
            aria-label={xmlExport ? 'Exporter XML' : 'Exporter ZIP'}
            title={xmlExport ? 'Exporter XML' : 'Exporter ZIP'}
            disabled={exportDisabled}
            onClick={onExport}
          />
          <AppButton
            size="sm"
            variant="outline-danger"
            icon="mdi:delete-sweep-outline"
            aria-label="Vider la sélection"
            title="Vider la sélection"
            disabled={busy || (tab === 'commercants' ? !commercants.length : (tab === 'offres' ? !offres.length : !dates.length))}
            onClick={onClearSelection}
          />
        </div>
      </div>

      <div className="card-body">
        {loading ? (
          <AppEmptyState loading />
        ) : tab === 'commercants' ? (
          commercants.length === 0 ? (
            <AppEmptyState
              message="Aucun commerçant pour ce mois."
              actions={[{
                key: 'propose',
                label: 'Générer une proposition',
                icon: 'mdi:auto-fix',
                variant: 'primary',
                onClick: onPropose,
                disabled: busy,
              }]}
            />
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndCommercants}>
              <SortableContext
                items={commercants.map((c) => String(c.id))}
                strategy={rectSortingStrategy}
                disabled={!editable || busy}
              >
                <div className="klub-magazine-grid klub-magazine-grid--commercants">
                  {commercants.map((row) => {
                    const statut = resolveCommercantStatut(row);
                    return (
                      <SortableMagazineCard key={row.id} id={String(row.id)} disabled={!editable || busy}>
                        <NouveauToggle
                          active={row.is_nouveau}
                          editable={editable}
                          disabled={busy}
                          onClick={() => onToggleNouveau('commercant', row)}
                        />
                        <div className="klub-magazine-card__media" title={row.photo_path || undefined}>
                          <MagazineMedia path={row.photo_path} fallbackIcon="mdi:account-tie" alt={row.nom || ''} />
                        </div>
                        <div className="klub-magazine-card__body">
                          <MagazineInlineText
                            value={row.nom}
                            className="klub-magazine-card__title"
                            disabled={!editable || busy}
                            onCommit={(value) => onInlineSave('commercant', row, 'nom', value)}
                          />
                          <MagazineInlineText
                            value={row.activite}
                            className="klub-magazine-card__meta"
                            placeholder="Activité"
                            disabled={!editable || busy}
                            onCommit={(value) => onInlineSave('commercant', row, 'activite', value)}
                          />
                          <MagazineInlineText
                            value={row.complement}
                            className="klub-magazine-card__meta"
                            placeholder="Complément"
                            disabled={!editable || busy}
                            onCommit={(value) => onInlineSave('commercant', row, 'complement', value)}
                          />
                          <MagazineInlineText
                            label="Copyright"
                            value={row.copyright}
                            className="klub-magazine-card__meta"
                            disabled={!editable || busy}
                            onCommit={(value) => onInlineSave('commercant', row, 'copyright', value)}
                          />
                        </div>
                        <div className="klub-magazine-card__actions">
                          <AppButton size="sm" variant="outline-secondary" icon="mdi:pencil" aria-label="Éditer" disabled={!editable || busy} onClick={() => onEditCommercant(row)} />
                          <AppButton size="sm" variant="outline-danger" icon="mdi:delete-outline" aria-label="Retirer" disabled={!editable || busy} onClick={() => onRemoveCommercant(row)} />
                          {statut ? (
                            <span className="klub-magazine-card__statut klub-magazine-card__statut--inline">
                              <AppBadge variant={bucketVariant(statut)} pill>
                                {BUCKET_LABELS[statut] || statut}
                              </AppBadge>
                            </span>
                          ) : null}
                        </div>
                      </SortableMagazineCard>
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          )
        ) : tab === 'offres' ? (
          offres.length === 0 ? (
            <AppEmptyState
              message="Aucune offre pour ce mois."
              actions={[{
                key: 'propose',
                label: 'Générer une proposition',
                icon: 'mdi:auto-fix',
                variant: 'primary',
                onClick: onPropose,
                disabled: busy,
              }]}
            />
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndOffres}>
              <SortableContext
                items={offres.map((o) => String(o.id))}
                strategy={rectSortingStrategy}
                disabled={!editable || busy}
              >
                <div className="klub-magazine-grid">
                  {offres.map((row) => {
                    const remiseLabel = formatRemise(row);
                    const statut = resolveOffreStatut(row);
                    return (
                      <SortableMagazineCard key={row.id} id={String(row.id)} disabled={!editable || busy}>
                        <NouveauToggle
                          active={row.is_nouveau}
                          editable={editable}
                          disabled={busy}
                          onClick={() => onToggleNouveau('offre', row)}
                        />
                        <MagazineInlineText
                          value={Number(row.remise) > 0 ? row.remise : ''}
                          displayValue={remiseLabel || ''}
                          placeholder="Remise"
                          className="klub-magazine-card__remise"
                          emptyClassName="klub-magazine-card__remise--empty"
                          type="number"
                          disabled={!editable || busy}
                          onCommit={(value) => onInlineSave('offre', row, 'remise', value)}
                        />
                        <div className="klub-magazine-card__media" title={row.photo_path || undefined}>
                          <MagazineMedia path={row.photo_path} fallbackIcon="mdi:tag-outline" alt={row.commercant_label || ''} />
                          <OfferPriceTag
                            row={row}
                            editable={editable}
                            busy={busy}
                            onCommit={(field, value) => onInlineSave('offre', row, field, value)}
                          />
                        </div>
                        <div className="klub-magazine-card__body">
                          <MagazineInlineText
                            value={row.commercant_label}
                            className="klub-magazine-card__title"
                            disabled={!editable || busy}
                            onCommit={(value) => onInlineSave('offre', row, 'commercant_label', value)}
                          />
                          <MagazineInlineText
                            value={row.description}
                            className="klub-magazine-card__meta"
                            multiline
                            rows={3}
                            disabled={!editable || busy}
                            onCommit={(value) => onInlineSave('offre', row, 'description', value)}
                          />
                          <MagazineInlineText
                            label="À partir de"
                            value={row.apartirde}
                            className="klub-magazine-card__meta"
                            disabled={!editable || busy}
                            onCommit={(value) => onInlineSave('offre', row, 'apartirde', value)}
                          />
                          <MagazineInlineText
                            label="Copyright"
                            value={row.copyright}
                            className="klub-magazine-card__meta"
                            disabled={!editable || busy}
                            onCommit={(value) => onInlineSave('offre', row, 'copyright', value)}
                          />
                        </div>
                        <div className="klub-magazine-card__actions">
                          <AppButton size="sm" variant="outline-secondary" icon="mdi:pencil" aria-label="Éditer" disabled={!editable || busy} onClick={() => onEditOffre(row)} />
                          <AppButton size="sm" variant="outline-danger" icon="mdi:delete-outline" aria-label="Retirer" disabled={!editable || busy} onClick={() => onRemoveOffre(row)} />
                          {statut ? (
                            <span className="klub-magazine-card__statut klub-magazine-card__statut--inline">
                              <AppBadge variant={bucketVariant(statut)} pill>
                                {BUCKET_LABELS[statut] || statut}
                              </AppBadge>
                            </span>
                          ) : null}
                        </div>
                      </SortableMagazineCard>
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          )
        ) : dates.length === 0 ? (
          <AppEmptyState
            message="Aucune date pour ce mois."
            actions={[{
              key: 'propose',
              label: 'Générer une proposition',
              icon: 'mdi:auto-fix',
              variant: 'primary',
              onClick: onPropose,
              disabled: busy,
            }]}
          />
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndDates}>
            <SortableContext
              items={dates.map((d) => String(d.id))}
              strategy={rectSortingStrategy}
              disabled={!editable || busy}
            >
              <div className="klub-magazine-grid">
                {dates.map((row) => (
                  <SortableMagazineCard key={row.id} id={String(row.id)} disabled={!editable || busy}>
                    <div className="klub-magazine-card__media" title={row.image_path || undefined}>
                      <MagazineMedia
                        path={row.image_path}
                        resolveUrl={resolveAgendaMediaUrl}
                        fallbackIcon="mdi:calendar-month"
                        alt={row.titre || ''}
                      />
                      {(editable || hasAmount(row.prix)) ? (
                        <MagazineInlineText
                          value={row.prix}
                          displayValue={formatPrix(row.prix)}
                          className="klub-magazine-card__price-tag"
                          emptyClassName="is-empty"
                          placeholder="Prix"
                          disabled={!editable || busy}
                          onCommit={(value) => onInlineSave('date', row, 'prix', value)}
                        />
                      ) : null}
                    </div>
                    <div className="klub-magazine-card__body">
                      <MagazineInlineText
                        value={row.titre}
                        className="klub-magazine-card__title"
                        disabled={!editable || busy}
                        onCommit={(value) => onInlineSave('date', row, 'titre', value)}
                      />
                      <MagazineInlineText
                        value={row.date_label}
                        className="klub-magazine-card__meta"
                        placeholder="Date"
                        disabled={!editable || busy}
                        onCommit={(value) => onInlineSave('date', row, 'date_label', value)}
                      />
                      <MagazineInlineText
                        value={row.thematique}
                        className="klub-magazine-card__meta"
                        placeholder="Thématique"
                        disabled={!editable || busy}
                        onCommit={(value) => onInlineSave('date', row, 'thematique', value)}
                      />
                      <MagazineInlineText
                        value={row.lieu}
                        className="klub-magazine-card__meta"
                        placeholder="Lieu"
                        disabled={!editable || busy}
                        onCommit={(value) => onInlineSave('date', row, 'lieu', value)}
                      />
                      <MagazineInlineText
                        value={row.resume}
                        className="klub-magazine-card__resume"
                        emptyClassName="klub-magazine-card__resume--empty"
                        placeholder="Pas encore de résumé"
                        multiline
                        rows={4}
                        disabled={!editable || busy}
                        onCommit={(value) => onInlineSave('date', row, 'resume', value)}
                      />
                      <MagazineInlineText
                        label="Infos"
                        value={row.infos}
                        className="klub-magazine-card__meta"
                        disabled={!editable || busy}
                        onCommit={(value) => onInlineSave('date', row, 'infos', value)}
                      />
                    </div>
                    <div className="klub-magazine-card__actions">
                      <KoikispassDateLink row={row} />
                      <AppButton size="sm" variant="outline-secondary" icon="mdi:pencil" aria-label="Éditer" title="Éditer" disabled={!editable || busy} onClick={() => onEditDate(row)} />
                      <AppButton
                        size="sm"
                        variant="outline-danger-600"
                        icon="mdi:robot-outline"
                        aria-label="Réécrire avec l'IA"
                        title="Réécrire avec l'IA"
                        disabled={!editable || busy}
                        onClick={() => onRewriteDate?.(row)}
                      />
                      <AppButton size="sm" variant="outline-danger" icon="mdi:delete-outline" aria-label="Retirer" title="Retirer" disabled={!editable || busy} onClick={() => onRemoveDate(row)} />
                    </div>
                  </SortableMagazineCard>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
}

export default KlubMagazinePanel;
